import { useEffect, useState } from "react";
import { Bell, X, Check, Loader2, Sparkles } from "lucide-react";
import {
  isPushSupported, permissionState, hasAskedBefore, markAsked,
  subscribePush,
} from "@/saas/lib/push";
import { useSaasAuth } from "@/saas/context/SaasAuthContext";

/**
 * Phase 10 — Push permission prompt.
 *
 * Custom in-page prompt (NOT the raw browser dialog) — shown the first time
 * an authenticated customer lands on the dashboard. Asks two things in one
 * shot:
 *   - "Yeni maçlar ve kampanya bildirimleri almak ister misiniz?"
 *   - Optional: "Pazarlama e-postaları da göndermek ister misiniz?" toggle
 *
 * On accept, triggers the native permission flow and POSTs the subscription.
 * On dismiss, sets a localStorage flag so we don't re-ask aggressively.
 *
 * data-testid: push-prompt, push-prompt-allow, push-prompt-dismiss,
 *   push-prompt-marketing.
 */
export default function PushPermissionPrompt() {
  const { token } = useSaasAuth();
  const [visible, setVisible] = useState(false);
  const [marketing, setMarketing] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // null | 'ok' | 'denied'
  const [err, setErr]   = useState("");

  useEffect(() => {
    if (!isPushSupported()) return;
    if (permissionState() !== "default") return; // already granted/denied
    if (hasAskedBefore()) return;
    // Show after a short delay so it doesn't feel pushy on initial load
    const t = setTimeout(() => setVisible(true), 1800);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const accept = async () => {
    setBusy(true); setErr("");
    const res = await subscribePush({ token, marketingOptIn: marketing });
    setBusy(false);
    if (!res.ok) { setErr(res.error); setDone("denied"); markAsked(); return; }
    setDone("ok");
    setTimeout(() => setVisible(false), 1800);
  };

  const dismiss = () => {
    markAsked();
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[90] w-[min(92vw,460px)]
                 bg-surface/95 backdrop-blur-xl border border-gold/30
                 rounded-2xl shadow-[0_30px_80px_-20px_rgba(245,166,35,0.45)]
                 p-5 animate-[slideUp_.4s_ease-out]"
      data-testid="push-prompt"
      role="dialog"
      aria-live="polite"
    >
      {!done && (
        <>
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gold/15 border border-gold/30 grid place-items-center shrink-0">
              <Bell className="w-5 h-5 text-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-base text-white tracking-tight leading-tight">
                Maç & Kampanya Bildirimleri
              </h3>
              <p className="text-xs text-white/60 mt-1 leading-relaxed">
                Yeni maçlar başlarken ve özel kampanyalar yayınlandığında
                anında haberdar olmak ister misiniz?
              </p>
            </div>
            <button
              type="button"
              onClick={dismiss}
              className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 grid place-items-center text-white/60 hover:text-white transition shrink-0"
              data-testid="push-prompt-dismiss"
              aria-label="Kapat"
              disabled={busy}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer select-none mb-4 px-1">
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              className="mt-0.5 w-4 h-4 shrink-0 accent-gold"
              data-testid="push-prompt-marketing"
              disabled={busy}
            />
            <span className="text-[12px] text-white/65 leading-relaxed">
              Yeni içerikler ve özel indirimlerden e-posta ile de haberdar
              olmak istiyorum.{" "}
              <span className="text-[10px] font-mono text-white/35 uppercase tracking-widest">KVKK uyumlu</span>
            </span>
          </label>

          {err && (
            <div className="text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2 mb-3" data-testid="push-prompt-err">
              {err}
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={dismiss}
              className="flex-1 h-10 rounded-md border border-white/15 bg-transparent text-white/70 hover:bg-white/5 text-xs font-semibold transition"
              data-testid="push-prompt-later"
              disabled={busy}
            >
              Daha sonra
            </button>
            <button
              type="button"
              onClick={accept}
              disabled={busy}
              className="flex-[1.4] h-10 rounded-md bg-gold text-black hover:bg-gold-hover text-xs font-bold tracking-tight transition active:translate-y-[1px] flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="push-prompt-allow"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {busy ? "Kaydediliyor…" : "İzin Ver & Devam Et"}
            </button>
          </div>
        </>
      )}

      {done === "ok" && (
        <div className="flex items-center gap-3 py-1" data-testid="push-prompt-ok">
          <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-500/40 grid place-items-center">
            <Check className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <div className="font-bold text-sm text-white">Aboneliğiniz aktif</div>
            <div className="text-xs text-white/55">Önemli bildirimleri size ileteceğiz.</div>
          </div>
        </div>
      )}
    </div>
  );
}
